import { CUSTOM_EVENT_HIGHLIGHT_CLICK } from '../../constants'
import { IPDFCompareChangeItem, TBbox } from '../types'

/**
 * Highlight layer class names for PDF comparison
 */
const PDF_HIGHLIGHT_LAYER_CLASS = 'pdf-compare-highlight-layer'
const PDF_HIGHLIGHT_BOX_CLASS = 'pdf-compare-highlight-box'

/**
 * Ensure a highlight layer exists on the page container.
 */
const ensureHighlightLayer = (pageContainer: HTMLElement) => {
  let layer = pageContainer.querySelector<HTMLDivElement>(
    `.${PDF_HIGHLIGHT_LAYER_CLASS}`
  )
  if (!layer) {
    layer = document.createElement('div')
    layer.className = PDF_HIGHLIGHT_LAYER_CLASS
    Object.assign(layer.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
    })
    pageContainer.style.position = 'relative'
    pageContainer.appendChild(layer)
  }

  return layer
}

/**
 * Clear all highlights from the page container.
 */
const clearHighlights = (pageContainer: HTMLElement) => {
  const layer = pageContainer.querySelector(`.${PDF_HIGHLIGHT_LAYER_CLASS}`)
  if (!layer) return

  layer.innerHTML = ''
}

const createHighlightBox = (params: {
  bbox: TBbox
  scale: number
  uuid?: string
  isSource?: boolean
  isTarget?: boolean
}) => {
  const { bbox, scale, uuid, isSource } = params
  const [x0, y0, x1, y1] = bbox
  const left = Math.min(x0, x1) * scale
  const top = Math.min(y0, y1) * scale
  const width = Math.abs(x1 - x0) * scale
  const height = Math.abs(y1 - y0) * scale

  const box = document.createElement('div')
  box.className = PDF_HIGHLIGHT_BOX_CLASS
  box.dataset.uuid = uuid
  Object.assign(box.style, {
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    background: isSource ? '#fdc9c580' : '#a3d3ff80',
    pointerEvents: 'auto',
    cursor: 'pointer',
  })

  if (uuid) {
    box.addEventListener('click', (event) => {
      event.stopPropagation()
      const highlightEvent = new CustomEvent(CUSTOM_EVENT_HIGHLIGHT_CLICK, {
        detail: { uuid },
      })
      globalThis.dispatchEvent(highlightEvent)
    })
  }

  return box
}

/**
 * Apply PDF highlights for a specific page.
 */
export const applyPdfDiffHighlights = (params: {
  pageContainer: HTMLElement
  diffOps: IPDFCompareChangeItem[]
  pageNumber: number
  scale: number
  isSource?: boolean
  isTarget?: boolean
}) => {
  const { pageContainer, diffOps, pageNumber, scale, isSource, isTarget } =
    params
  if (!pageContainer) return
  if (!diffOps || diffOps.length === 0) {
    clearHighlights(pageContainer)
    return
  }

  const layer = ensureHighlightLayer(pageContainer)
  layer.innerHTML = ''

  const highlights = diffOps.filter((diff) => diff.pageIndex === pageNumber)
  highlights.forEach((diff) => {
    const bbox = isSource ? diff.sourceBbox : diff.targetBbox
    if (!bbox) return

    const box = createHighlightBox({
      bbox,
      scale,
      uuid: diff.uuid,
      isSource,
      isTarget,
    })
    layer.appendChild(box)
  })
}
